import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Rocket, Link2, Sparkles, QrCode, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/layout/Logo";

const inputCls = "h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm outline-none focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-200";

const STEPS = ["Business details", "Connect Google", "Review link", "AI preferences", "Finish"];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ phone: "", website: "", reviewLink: "", tone: "professional", language: "english" });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  const finish = async () => {
    try {
      await api.put("/settings", {
        businessInfo: { phone: data.phone, website: data.website, reviewLink: data.reviewLink },
        aiSettings: { tone: data.tone, language: data.language },
        client: { reviewLink: data.reviewLink, phone: data.phone },
      });
      toast.success("Setup complete! Welcome aboard 🎉");
      navigate("/app");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-appbg px-6 py-10">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-xl">
        <div className="mb-6 flex items-center justify-between">
          <Logo />
          <span className="text-sm font-semibold text-brand-600">{progress}% complete</span>
        </div>
        <div className="mb-6 h-2 w-full overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full gradient-brand transition-all" style={{ width: `${progress}%` }} /></div>

        <div className="card-surface p-8">
          <p className="text-overline">Step {step + 1} of {STEPS.length}</p>
          <h2 className="mt-1 font-heading text-2xl font-bold text-slate-900">{STEPS[step]}</h2>

          <div className="mt-6 min-h-[180px]">
            {step === 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-brand-600"><Building2 className="h-5 w-5" /><span className="text-sm">Tell us about your business</span></div>
                <input className={inputCls} placeholder="Business phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} />
                <input className={inputCls} placeholder="Website (optional)" value={data.website} onChange={(e) => set("website", e.target.value)} />
              </div>
            )}
            {step === 1 && (
              <div className="space-y-4 text-center">
                <Link2 className="mx-auto h-10 w-10 text-brand-500" />
                <p className="text-sm text-slate-600">Connect your Google Business Profile to schedule posts and sync reviews. You can do this now or later.</p>
                <Button variant="outline" className="rounded-xl" onClick={() => navigate("/app/google")}>Open Google Connection</Button>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-brand-600"><QrCode className="h-5 w-5" /><span className="text-sm">Add your Google review link</span></div>
                <input className={inputCls} placeholder="https://g.page/r/your-business/review" value={data.reviewLink} onChange={(e) => set("reviewLink", e.target.value)} />
                <p className="text-xs text-slate-400">We use this to generate your review QR code and review request messages.</p>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-brand-600"><Sparkles className="h-5 w-5" /><span className="text-sm">Choose your default AI tone & language</span></div>
                <Select value={data.tone} onValueChange={(v) => set("tone", v)}><SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{["professional", "friendly", "premium"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
                <Select value={data.language} onValueChange={(v) => set("language", v)}><SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{["english", "hinglish", "hindi"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
            {step === 4 && (
              <div className="space-y-3 text-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-green-600"><Check className="h-8 w-8" /></div>
                <p className="font-heading text-lg font-semibold text-slate-800">You're all set!</p>
                <p className="text-sm text-slate-500">Your dashboard is ready. Start managing your Google Business Profile like a pro.</p>
              </div>
            )}
          </div>

          <div className="mt-8 flex justify-between">
            <Button variant="ghost" className="rounded-xl" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
            {step < STEPS.length - 1 ? (
              <Button className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={() => setStep((s) => s + 1)} data-testid="onboarding-next-btn">Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
            ) : (
              <Button className="rounded-xl bg-brand-700 text-white hover:bg-brand-800" onClick={finish} data-testid="onboarding-finish-btn"><Rocket className="mr-1 h-4 w-4" /> Go to Dashboard</Button>
            )}
          </div>
          <button onClick={() => navigate("/app")} className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600">Skip setup</button>
        </div>
      </motion.div>
    </div>
  );
}
